import { Link } from "react-router-dom";
import { ArrowUpRight, Mail, MessageCircle } from "lucide-react";
import { SITE, whatsappLink } from "@/lib/site";
import Reveal from "@/components/Reveal";

const UPDATED = "11 October 2026";

function Section({ n, title, children }) {
  return (
    <Reveal>
      <section data-testid={`policy-section-${n}`}>
        <h2 className="font-display text-2xl font-semibold text-ink">
          <span className="mr-2 font-mono text-sm text-gold">{n}.</span>
          {title}
        </h2>
        <div className="mt-3 space-y-3 text-sm leading-relaxed text-ink2">{children}</div>
      </section>
    </Reveal>
  );
}

function PolicyLayout({ testId, tagline, title, children }) {
  return (
    <main className="min-h-screen bg-bg" data-testid={testId}>
      <div className="mx-auto max-w-3xl px-4 py-16 sm:px-8 lg:py-24">
        <Reveal>
          <p className="overline-tag font-semibold">{tagline}</p>
          <h1 className="mt-3 font-display text-4xl font-semibold text-ink sm:text-5xl">{title}</h1>
          <p className="mt-3 font-mono text-xs text-ink3">Last updated: {UPDATED}</p>
        </Reveal>
        <div className="mt-12 space-y-9">{children}</div>

        <div className="gold-hr my-12" />

        <Reveal>
          <div className="flex flex-wrap gap-3" data-testid="policy-cross-links">
            <Link to="/policies/refund-return" className="btn-ghost-gold !px-4 !py-2 !text-[10px]">Refund &amp; Return Policy</Link>
            <Link to="/policies/privacy" className="btn-ghost-gold !px-4 !py-2 !text-[10px]">Privacy Policy</Link>
            <Link to="/policies/terms" className="btn-ghost-gold !px-4 !py-2 !text-[10px]">Terms of Service</Link>
          </div>
          <div className="mt-8 card-surface p-6" data-testid="policy-contact-block">
            <p className="overline-tag font-semibold">Talk to us</p>
            <p className="mt-3 text-sm text-ink2">
              Sashti Jewels · {SITE.domain}
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <a href={`mailto:${SITE.email}`} className="btn-ghost-gold !px-4 !py-2 !text-[10px]">
                <Mail size={13} /> {SITE.email}
              </a>
              <a href={whatsappLink("Hello Sashti Jewels, I have a question about my order.")} target="_blank" rel="noopener noreferrer" className="btn-ghost-gold !px-4 !py-2 !text-[10px]">
                <MessageCircle size={13} /> WhatsApp — {SITE.whatsappDisplay}
              </a>
            </div>
          </div>
          <p className="mt-6 text-center font-mono text-[11px] text-ink3">
            <Link to="/shop" className="hover:text-gold transition-colors">← Back to shopping</Link>
          </p>
        </Reveal>
      </div>
    </main>
  );
}

export function RefundReturnPolicy() {
  return (
    <PolicyLayout testId="refund-policy-page" tagline="Customer Care" title="Refund &amp; Return Policy">
      <Section n={1} title="What this policy covers">
        <p>
          Every Sashti Jewels piece reaches you after a careful quality check. Even so, if something goes wrong
          with your order, this policy explains exactly when we can offer a replacement or a refund and how to
          raise a claim with us.
        </p>
      </Section>

      <Section n={2} title="When a return or replacement is possible">
        <p>
          Returns are accepted only if your piece arrives damaged, defective or if you received the wrong item.
          We are unable to accept returns for a change of mind, a preference for a different design, size or
          colour, or for general wear and tear that happens after delivery.
        </p>
        <p>
          Custom and made-to-order pieces created through our Custom Atelier are crafted exclusively for you and
          are strictly non-returnable and non-refundable, unless they arrive damaged.
        </p>
      </Section>

      <Section n={3} title="Please report issues within 24 hours">
        <p>
          Any damage or defect must be reported to us within 24 hours of delivery — message us on WhatsApp at
          {` ${SITE.whatsappDisplay} `}or email {SITE.email}. Claims made after 24 hours cannot be entertained.
        </p>
        <p>
          To process your claim, please share a clear, unedited unboxing video showing the sealed parcel from all
          sides, the unsealing in one continuous take, and the damaged or defective piece clearly. Claims without
          valid unboxing proof cannot be accepted.
        </p>
      </Section>

      <Section n={4} title="Replacement first, refund if unavailable">
        <p>
          Where a claim is approved, we will replace the piece with the same product. If the same design is no
          longer available, we will issue a refund to your original payment method. Refunds are offered only when
          a replacement is not possible.
        </p>
      </Section>

      <Section n={5} title="How to send the item back">
        <p>
          We do not offer a pickup service, so we request you to ship the piece back to us. Please return it in
          its original packaging with all tags, pouches and any accessories included. Once we receive and inspect
          the item, we will confirm whether your claim is approved.
        </p>
        <p>
          The cost of sending the damaged piece back to us is borne by you; we cover the cost of shipping the
          replacement to your address.
        </p>
      </Section>

      <Section n={6} title="Refund timelines">
        <p>
          Once a return is approved, the replacement or refund process is completed within 7–10 working days.
          Refunds are made through the same channel used for the original payment (for example, via Razorpay to
          your UPI, card or bank account). Your bank may take an additional 2–3 working days to reflect the credit.
        </p>
      </Section>

      <Section n={7} title="What is not covered">
        <p>
          Damage from mishandling, misuse, water or perfume exposure, or normal wear and tear is not eligible for
          return or replacement. Imitation jewellery naturally ages with use — the care card in your parcel tells
          you how to keep the antique finish beautiful for longer.
        </p>
      </Section>

      <Section n={8} title="Our decision and how to reach us">
        <p>
          All decisions on returns, replacements and refunds made by Sashti Jewels are final. If you have any
          question about a claim, write to {SITE.email} or WhatsApp {SITE.whatsappDisplay} — we are happy to help.
        </p>
      </Section>
    </PolicyLayout>
  );
}

export function PrivacyPolicy() {
  return (
    <PolicyLayout testId="privacy-policy-page" tagline="Your Data, Respected" title="Privacy Policy">
      <Section n={1} title="Who we are">
        <p>
          Sashti Jewels ({SITE.domain}) is an online store for antique-finish imitation jewellery. This policy
          explains what personal information we collect through our website, why we collect it, and the choices
          you have. By using our website, you agree to this policy alongside our{" "}
          <Link to="/policies/terms" className="text-gold underline underline-offset-2">Terms of Service</Link>.
        </p>
      </Section>

      <Section n={2} title="What we collect">
        <p>
          We collect only what we need to serve you: your name, email address, phone/WhatsApp number and delivery
          address when you place an order or send an enquiry; your order history and the reviews or custom-design
          briefs you choose to submit; and basic technical information (such as browser type and pages visited)
          that helps us keep the website working smoothly.
        </p>
      </Section>

      <Section n={3} title="Why we use it">
        <p>
          We use your information to deliver your orders and support you afterwards, to reply to enquiries and
          custom-design requests, to maintain records of transactions, to prevent fraud, and to improve the
          shopping experience. We do not sell your personal information — to anyone, ever.
        </p>
      </Section>

      <Section n={4} title="Payments">
        <p>
          Online payments are processed by Razorpay (UPI, cards and netbanking). Your card and UPI credentials go
          directly to Razorpay over an encrypted connection — they never touch our servers and we never see or
          store them. Razorpay shares with us only what we need to confirm your payment and issue refunds.
          Razorpay's own privacy policy applies to its handling of your payment information.
        </p>
      </Section>

      <Section n={5} title="Cookies and local storage">
        <p>
          Our website uses a small amount of browser storage and cookies for things that help you: remembering
          your shopping cart, keeping our admin users signed in, and remembering your theme preference. We do not
          use advertising or cross-site tracking cookies.
        </p>
      </Section>

      <Section n={6} title="Who else can see your data">
        <p>
          We share your information only with: Razorpay (to take and refund payments); our website hosting and
          storage platform, which keeps the website running and holds the data you submit; and delivery channels
          you choose, such as WhatsApp, when you tap a WhatsApp button. Where you follow a link to Instagram or
          another website, that platform's own privacy policy applies.
        </p>
      </Section>

      <Section n={7} title="How long we keep it">
        <p>
          Order and payment records are kept for as long as Indian tax and consumer-protection rules require.
          Enquiry and custom-design details are kept only as long as needed to serve you, after which they are
          removed. You may ask us to delete your personal information sooner, subject to these legal requirements.
        </p>
      </Section>

      <Section n={8} title="Your rights">
        <p>
          You can ask to see the personal information we hold about you, ask us to correct anything wrong, or ask
          us to delete it. Write to {SITE.email} or WhatsApp {SITE.whatsappDisplay} and we will respond promptly.
          If you are not satisfied with our response, you may escalate your concern under applicable Indian
          data-protection law.
        </p>
      </Section>

      <Section n={9} title="Children">
        <p>
          Our website is meant for adults. Purchases should be made by people aged 18 or above, and we do not
          knowingly collect personal information from children.
        </p>
      </Section>

      <Section n={10} title="Changes to this policy">
        <p>
          We may update this policy from time to time as our store evolves. The latest version will always be
          available on this page, with the date of the change shown at the top. Questions? We are one WhatsApp
          message away.
        </p>
      </Section>
    </PolicyLayout>
  );
}

export function TermsOfService() {
  return (
    <PolicyLayout testId="terms-policy-page" tagline="The Ground Rules" title="Terms of Service">
      <Section n={1} title="About these terms">
        <p>
          Welcome to Sashti Jewels! This website, {SITE.domain} (the "Services"), is operated by Sashti Jewels.
          The site is built and hosted on the Emergent platform, and online payments are handled by Razorpay.
          These Terms, together with our{" "}
          <Link to="/policies/privacy" className="text-gold underline underline-offset-2">Privacy Policy</Link> and{" "}
          <Link to="/policies/refund-return" className="text-gold underline underline-offset-2">Refund &amp; Return Policy</Link>,
          describe your rights and responsibilities when you shop with us. By browsing or ordering, you accept them.
        </p>
      </Section>

      <Section n={2} title="Our jewellery">
        <p>
          Sashti Jewels specialises in antique-finish imitation accessories — temple jewellery, harams, necklaces,
          chokers and more — finished to a traditional gold look. Our pieces are imitation jewellery and are NOT
          made of real gold, silver, or precious stones, and carry no hallmark. We describe every piece honestly
          and show real photographs, but colours can look slightly different from one screen to another.
        </p>
      </Section>

      <Section n={3} title="Placing an order">
        <p>
          When you place an order, you are making an offer to buy. We accept the offer once your payment is
          received and the order is confirmed by us (by email or WhatsApp). We may decline an order — for example,
          if a piece is out of stock or there is a pricing error — and we will refund any amount already paid in
          that case.
        </p>
      </Section>

      <Section n={4} title="Prices and payment">
        <p>
          Prices are shown in Indian Rupees and may change without notice; the price you pay is the one shown at
          checkout. Payments are taken securely through Razorpay (UPI, cards, netbanking). Your order is dispatched
          once the payment is realised in full.
        </p>
      </Section>

      <Section n={5} title="Shipping and delivery">
        <p>
          We dispatch to serviceable PIN codes in India and share tracking details where available. Delivery times
          are honest estimates, not guarantees — occasional delays by carriers or events beyond our control are
          possible. Risk in the goods passes to you on delivery.
        </p>
      </Section>

      <Section n={6} title="Returns and refunds">
        <p>
          Purchases are eligible for return or replacement strictly as described in our{" "}
          <Link to="/policies/refund-return" className="text-gold underline underline-offset-2">Refund &amp; Return Policy</Link> —
          in short, damaged or wrong items reported within 24 hours with unboxing proof.
        </p>
      </Section>

      <Section n={7} title="Custom Atelier orders">
        <p>
          Custom pieces made through our Custom Atelier are crafted to your brief, with the design and price
          confirmed with you before work begins. They are made exclusively for you and cannot be returned or
          refunded unless they arrive damaged. AI-styled model previews shown in the Atelier are indicative
          styling aids to help you visualise a design — the final handcrafted piece may vary slightly.
        </p>
      </Section>

      <Section n={8} title="Reviews and content you send us">
        <p>
          If you submit a review, photograph, custom-design brief or other content, you grant us permission to
          display it on our website and social channels. Please only send content you have the right to share, and
          keep it lawful and kind. We may remove content that is unlawful, abusive or misleading.
        </p>
      </Section>

      <Section n={9} title="Acceptable use">
        <p>
          Please use the website lawfully. Do not attempt to break into, overload, scrape or copy the site, place
          fraudulent orders, or use it to send spam or harmful code. We may suspend access to anyone who does.
        </p>
      </Section>

      <Section n={10} title="Ownership">
        <p>
          The Sashti Jewels name, logo, website design, product photography and text belong to Sashti Jewels and
          may not be copied or reused commercially without our written permission.
        </p>
      </Section>

      <Section n={11} title="Availability and errors">
        <p>
          Our catalog changes as pieces sell or new designs arrive. Occasionally there may be typing or pricing
          errors; if we spot one after your order, we will correct it and contact you before proceeding.
        </p>
      </Section>

      <Section n={12} title="Liability">
        <p>
          To the extent permitted by law, our liability for any order is limited to the value you paid for it, and
          we are not liable for indirect losses. Nothing in these terms limits your rights as a consumer under
          Indian law.
        </p>
      </Section>

      <Section n={13} title="Governing law">
        <p>
          These Terms are governed by the laws of India, and any dispute will be subject to the courts at our
          place of business in India.
        </p>
      </Section>

      <Section n={14} title="Changes and contact">
        <p>
          We may update these Terms by posting the new version on this page. Continued use of the website after a
          change means you accept it. Questions? Email {SITE.email} or WhatsApp {SITE.whatsappDisplay}.
        </p>
      </Section>
    </PolicyLayout>
  );
}
